"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useCart } from "@/components/cart/cart-context";
import { BasketIcon } from "../ui/Icons";
import { useLayoutData } from "../layout/LayoutDataProvider";

export default function CartBadgeClient() {
  const { templateRoutes } = useLayoutData();
  const basketHref = templateRoutes?.["page-basket.php"];

  const { itemsCount, isHydrated } = useCart();

  const shouldShowBadge = isHydrated && itemsCount > 0;
  const displayCount = itemsCount > 99 ? "99+" : String(itemsCount);

  const hasShownRef = useRef(false);
  const prevShouldShowRef = useRef(false);
  const [playFirstPop, setPlayFirstPop] = useState(false);

  const [prevDisplayCount, setPrevDisplayCount] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const prev = prevShouldShowRef.current;
    if (!prev && shouldShowBadge && !hasShownRef.current) {
      setPlayFirstPop(true);
      hasShownRef.current = true;
    }
    prevShouldShowRef.current = shouldShowBadge;
  }, [shouldShowBadge]);

  useEffect(() => {
    if (!shouldShowBadge) return;
    setPrevDisplayCount((currentPrev) => {
      if (currentPrev === null) return displayCount;
      if (currentPrev !== displayCount) {
        setIsTransitioning(true);
        if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => {
          setPrevDisplayCount(displayCount);
          setIsTransitioning(false);
        }, 220);
      }
      return currentPrev;
    });
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [displayCount, shouldShowBadge]);

  if (!basketHref) return null;

  return (
    <Link href={basketHref} className="relative inline-flex items-center">
      <BasketIcon className="h-5 w-5 text-current" />

      {shouldShowBadge && (
        <span
          className={[
            "absolute -right-4 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-fuji-red border-2 border-white text-[10px] font-semibold text-white overflow-hidden",
            playFirstPop ? "badge-first-pop" : "",
          ].join(" ")}
          onAnimationEnd={() => setPlayFirstPop(false)}
          aria-label={`${itemsCount} items in basket`}
        >
          <span className="relative block h-full w-full">
            {isTransitioning && prevDisplayCount !== null && (
              <span className="badge-count-out-up absolute inset-0 flex items-center justify-center">
                {prevDisplayCount}
              </span>
            )}
            <span
              key={displayCount}
              className={[
                "absolute inset-0 flex items-center justify-center",
                isTransitioning ? "badge-count-in-up" : "",
              ].join(" ")}
            >
              {displayCount}
            </span>
          </span>
        </span>
      )}
    </Link>
  );
}